import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Loader2, X } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useCountry } from "@/lib/CountryContext";

/**
 * Floating button + modal to save an email alert for new matching annonces.
 * Preset values can be passed to anchor the alert on a specific filter set.
 *
 * Example: <EmailAlertButton presetCategory="Immobilier" presetEtatBien="inachevee" />
 */
export default function EmailAlertButton({
  presetCategory = "",
  presetCity = "",
  presetEtatBien = "",
  presetTypeBien = "",
  presetStatut = "",
  presetLabel = "",
  buttonLabel = "Alertes email",
  testId = "email-alert-btn",
}) {
  const [open, setOpen] = useState(false);
  const { user, login } = useAuth();

  const onClick = () => {
    if (!user) {
      toast.info("Connectez-vous pour activer une alerte email.");
      login?.();
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-orange-300 text-orange-700 font-bold text-sm hover:bg-orange-50 transition shadow-sm"
        data-testid={testId}
      >
        <Bell size={14} /> {buttonLabel}
      </button>
      {open && (
        <EmailAlertModal
          onClose={() => setOpen(false)}
          presetCategory={presetCategory}
          presetCity={presetCity}
          presetEtatBien={presetEtatBien}
          presetTypeBien={presetTypeBien}
          presetStatut={presetStatut}
          presetLabel={presetLabel}
          userEmail={user?.email || ""}
        />
      )}
    </>
  );
}

function EmailAlertModal({
  onClose, presetCategory, presetCity, presetEtatBien,
  presetTypeBien, presetStatut, presetLabel, userEmail,
}) {
  const { country } = useCountry();
  const [label, setLabel] = useState(presetLabel || "Mes annonces Fodindin");
  const [email, setEmail] = useState(userEmail);
  const [city, setCity] = useState(presetCity);
  const [etatBien, setEtatBien] = useState(presetEtatBien);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/me/email-alerts`, {
        label, email,
        category: presetCategory,
        type_bien: presetTypeBien,
        statut: presetStatut,
        city, etat_bien: etatBien,
      }, { withCredentials: true });
      toast.success("Alerte activee. Vous recevrez un email hebdomadaire des nouvelles annonces.");
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Echec de la sauvegarde";
      toast.error(typeof msg === "string" ? msg : "Echec de la sauvegarde");
    } finally { setLoading(false); }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4 overflow-y-auto"
         style={{ zIndex: 9999 }} onClick={onClose} data-testid="email-alert-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl my-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-extrabold tracking-tight">Recevoir les nouvelles annonces</h3>
            <p className="text-xs text-[var(--ink-soft)] mt-1">
              Un email recapitulatif chaque semaine avec les nouveautes correspondant a vos criteres.
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-soft)] hover:text-[var(--ink)]"
                  data-testid="email-alert-close" type="button">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-3 mt-5" data-testid="email-alert-form">
          <Field label="Nom de l'alerte">
            <input value={label} onChange={(e) => setLabel(e.target.value)} required
                   className={inputCls} data-testid="email-alert-label" />
          </Field>
          <Field label="Email">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                   className={inputCls} data-testid="email-alert-email" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ville (optionnel)">
              <select value={city} onChange={(e) => setCity(e.target.value)} className={inputCls}
                      data-testid="email-alert-city">
                <option value="">Toutes les villes</option>
                {country.cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            {presetCategory === "Immobilier" && (presetTypeBien !== "terrain") && (
              <Field label="Etat (optionnel)">
                <select value={etatBien} onChange={(e) => setEtatBien(e.target.value)} className={inputCls}
                        data-testid="email-alert-etat">
                  <option value="">Tous etats</option>
                  <option value="sans_travaux">Sans travaux</option>
                  <option value="avec_travaux">Avec travaux</option>
                  <option value="inachevee">Inachevee</option>
                </select>
              </Field>
            )}
          </div>

          {presetCategory && (
            <div className="rounded-2xl bg-orange-50 p-3 text-xs text-orange-800">
              Filtre applique : <strong>{presetCategory}</strong>
              {presetTypeBien && <> · {presetTypeBien}</>}
              {presetStatut && <> · {presetStatut === "vente" ? "A vendre" : "A louer"}</>}
              {etatBien && <> · {etatBien.replace("_", " ")}</>}
              {city && <> · {city}</>}
            </div>
          )}

          <button type="submit" disabled={loading}
                  className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2"
                  data-testid="email-alert-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
            {loading ? "Activation..." : "Activer l'alerte"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--ink-soft)] block mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full !py-2.5 !px-3 rounded-xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition bg-white";
